// Account sign-in/sign-up, preserving only validated local MCP authorization requests.
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { signIn, signUp } from '@/lib/auth-client';
import { api } from '@/lib/api';
import { useWorkspaceStore, type Workspace } from '@/stores/workspace';

export function AuthPage() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const setCurrent = useWorkspaceStore((s) => s.setCurrent);
  const [mode, setMode] = useState<'signin' | 'signup'>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const inviteCode = params.get('code');
  const mcpRequest = params.get('mcp_request');
  const mcpReturn = mcpRequest && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(mcpRequest)
    ? `/mcp/authorize?request=${encodeURIComponent(mcpRequest)}` : null;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      if (mode === 'signup') {
        const { error } = await signUp.email({ email, password, name });
        if (error) throw new Error(error.message);
        if (mcpReturn) { navigate(mcpReturn, { replace: true }); return; }
        // New users always go to onboarding (or join with their invite code)
        navigate(inviteCode ? `/onboarding` : '/onboarding', { replace: true });
        return;
      }

      const { error } = await signIn.email({ email, password });
      if (error) throw new Error(error.message);
      if (mcpReturn) { navigate(mcpReturn, { replace: true }); return; }

      // Existing user: do they already belong to a workspace?
      // If yes and no pending invite, jump straight to /home; otherwise onboarding handles it.
      if (!inviteCode) {
        try {
          const workspaces = await api.get<Workspace[]>('/workspaces');
          if (workspaces.length > 0) {
            setCurrent(workspaces[0]);
            navigate('/home', { replace: true });
            return;
          }
        } catch {
          // fall through to onboarding
        }
      }
      navigate('/onboarding', { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : t('auth.errorGeneric'));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/30 p-4">
      <div className="w-full max-w-sm rounded-2xl border bg-card p-6 shadow-sm">
        <h1 className="mb-1 text-2xl font-semibold">{t('auth.title')}</h1>
        <p className="mb-6 text-sm text-muted-foreground">
          {mode === 'signin' ? t('auth.subtitleSignIn') : t('auth.subtitleSignUp')}
        </p>

        <form onSubmit={handleSubmit} className="space-y-3">
          {mode === 'signup' && (
            <input
              type="text"
              autoComplete="name"
              placeholder={t('auth.fieldName')}
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              className="w-full rounded-md border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-ring"
            />
          )}
          <input
            type="email"
            autoComplete="email"
            placeholder={t('auth.fieldEmail')}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="w-full rounded-md border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-ring"
          />
          <input
            type="password"
            autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
            placeholder={t('auth.fieldPasswordHint')}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={8}
            className="w-full rounded-md border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-ring"
          />

          {error && <div className="rounded-md bg-destructive/10 p-2 text-xs text-destructive">{error}</div>}

          <button
            type="submit"
            disabled={loading}
            className="flex min-h-[44px] w-full items-center justify-center rounded-md bg-primary text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
          >
            {loading
              ? t('auth.submitting')
              : mode === 'signin'
              ? t('auth.submitSignIn')
              : t('auth.submitSignUp')}
          </button>
        </form>

        <button
          type="button"
          onClick={() => setMode(mode === 'signin' ? 'signup' : 'signin')}
          className="mt-4 w-full text-sm text-muted-foreground hover:text-foreground"
        >
          {mode === 'signin' ? t('auth.switchToSignUp') : t('auth.switchToSignIn')}
        </button>
      </div>
    </div>
  );
}
